import { VoidCallback } from '@deqo/foundation';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocalStorage, useSessionStorage } from 'react-use';
import styled, { css } from 'styled-components';
import { Link } from 'wouter';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors, Menu } from '../../../ui';
import { FixedScroller } from '../../../ui/Scrollers';
import { AppLocation } from '../../AppLocation';
import { AppNavigator } from '../../AppNavigator';
import { useResolve, useResolveMaybe } from '../../Container';
import { useOnCreateTeam } from '../../actions/WorkspaceActions';
import { Popover } from '../../canvas/ui/Popover';
import { useFeatureCheck } from '../../common/FeatureElements';
import { usePermissionCheck } from '../../common/PermissionElements';
import { WorkspaceModel } from '../../dashboard/Models';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { AccountModel } from '../AccountModel';

const Button = styled.div<{ open?: boolean, active?: boolean }>`
    position: relative;
    height: 2.5rem;
    border-radius: .25rem;
    border: .125rem solid ${Colors.borderLight};
    color: ${p => p.active ? Colors.primary : Colors.text};
    font-weight: 420;
    cursor: pointer;

    &:hover {
        border: .125rem solid ${Colors.primary};
    }

    &:active {
        border: .125rem solid ${Colors.primary};
        background: #fcfcfc;
    }

    ${p => p.open && css`
        border: .125rem solid ${Colors.primary};
    `};

    span {
        display: block;
        position: absolute;
        top: 50%;
        left: .5rem;
        right: 2rem;
        transform: translateY(-50%);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    svg {
        position: absolute;
        top: 50%;
        right: .5rem;
        transform: translateY(-50%);
    }
`;

const WorkspaceMenu = ({ onClose }: { onClose: VoidCallback }) => {

    const nav = useResolve(AppNavigator);
    const { workspaces } = useObserver(useResolve(AccountModel));
    const active = useObserver(useResolve(WorkspaceModel));
    const [_1, setActive] = useLocalStorage('deqo/ui/workspace');
    const [_2, setActiveTemp] = useSessionStorage('deqo/ui/workspace');

    const canManageTeams = usePermissionCheck('ac_team:write');
    const canPrivateBoards = useFeatureCheck('privateBoards');
    const { canCreateWorkspace, onCreateWorkspace } = useOnCreateTeam({ onInvoke: onClose, autoOpen: true });

    const teamWorkspaces = useMemo(() => (
        workspaces.filter(x => x.type === 'team').sort((a, b) => a.label?.localeCompare(b.label))
    ), [workspaces]);
    const userWorkspaces = useMemo(() => (
        workspaces.filter(x => x.type === 'user')
    ), [workspaces]);

    const gotoWorkspace = useCallback((id: string) => {
        onClose();
        setActive(id);
        setActiveTemp(id);
        nav.go(`~/${id}/boards`);
    }, [onClose]);

    return <Flexer maxWidth="20" minWidth="16">
        <FixedScroller maxHeight="24">
            <Menu>
                {teamWorkspaces.map(t =>
                    <Menu.Item key={t.id}
                        checked={t.id === active.id}
                        onClick={() => gotoWorkspace(t.id)} >
                        <Menu.ItemTextCropped>{t.label}</Menu.ItemTextCropped>
                    </Menu.Item>
                )}
                {userWorkspaces.map(t =>
                    <Menu.Item key={t.id}
                        checked={t.id === active.id}
                        onClick={() => gotoWorkspace(t.id)}>
                        <Menu.ItemTextCropped>Private Boards</Menu.ItemTextCropped>
                    </Menu.Item>
                )}
                {userWorkspaces.length === 0 && canPrivateBoards &&
                    <Link to='~/private'>
                        <Menu.Item>
                            <Menu.ItemTextCropped>Private Boards</Menu.ItemTextCropped>
                        </Menu.Item>
                    </Link>
                }
                {(canManageTeams || canCreateWorkspace) && <>
                    <Menu.Divide></Menu.Divide>
                    {canManageTeams &&
                        <Link to='~/manage/teams'>
                            <Menu.Item>
                                <Menu.ItemTextCropped>Manage Teams</Menu.ItemTextCropped>
                            </Menu.Item>
                        </Link>
                    }
                    {canCreateWorkspace &&
                        <Menu.Item onClick={onCreateWorkspace}>
                            <Menu.ItemTextCropped>Create Team</Menu.ItemTextCropped>
                        </Menu.Item>
                    }
                </>}
            </Menu>
        </FixedScroller>
    </Flexer>;
};

const useIsUserInWorkspaceView = () => {
    const location = useObserver(useResolve(AppLocation));
    const { name, value } = location[1] ?? {};
    if (name === 'boards') return true;
    if (name === 'view' && value === 'tasks') return true;
    if (name === 'view' && value === 'members') return true;
    return false;
};

export const WorkspaceSelector = React.memo(() => {

    const active = useObserver(useResolveMaybe(WorkspaceModel));
    const label = active?.type === 'user' ? 'Private Boards' : active?.label ?? 'None';
    const inWorkspace = useIsUserInWorkspaceView();

    const [open, setOpen] = useState(false);
    const onClose = useCallback(() => setOpen(false), []);

    return <Flexer >
        <Popover
            block
            open={open}
            onOpenChange={setOpen}
            inner={{ direction: 'down' }}
            placement="bottom-end"
            content={<WorkspaceMenu onClose={onClose} />}>
            <Button open={open} active={inWorkspace} style={{ width: '100%' }}>
                <span>{label}</span>
                <Icon size="1" src="deqo://icons/hero/chevron-down" />
            </Button>
        </Popover>
    </Flexer>;

});
